import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { SchemaLink } from 'apollo-link-schema';
import get from 'lodash.get';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { Cookies } from 'react-cookie';
import createCache from '../data/cache';
import report from '../errorReporting';
let authToken = null;
const cookies = new Cookies();
export const setAuthToken = (newToken) => {
    authToken = newToken;
    let expires = new Date();
    expires.setMonth(new Date().getMonth() + 1);
    cookies.set('authToken', newToken, {
        expires,
        path: '/',
        sameSite: true
    });
};
export const loadAuthToken = () => {
    const cookieAuthToken = cookies.get('authToken');
    if (cookieAuthToken) {
        authToken = cookieAuthToken;
    }
};
export const clearAuthToken = () => {
    authToken = null;
    if (cookies.get('authToken')) {
        cookies.remove('authToken', { path: '/' });
    }
};
let preview = false;
export const setPreview = (usePreview) => {
    preview = usePreview;
};
let channel;
export const setChannel = (newChannel) => {
    channel = newChannel;
};
export const createClient = ({ apolloConfig: { token, shopid, enableGateway, graphQLURI }, schema }) => {
    if (!token || !shopid) {
        throw new Error(`Missing ${!token && `token`} ${!shopid && `shopid`}`);
    }
    const createGatewayLink = () => __IN_SERVER__
        ? schema
            ? new SchemaLink({
                schema,
                context: {
                    headers: {
                        currency: channel.currency.id,
                        channelid: channel.id,
                        culture: channel.language.culture,
                        country: channel.country.code,
                        name: channel.name,
                        authorization: authToken ? `Bearer ${authToken}` : '',
                        preview
                    }
                }
            })
            : createHttpLink({
                uri: `http://localhost:${process.env.PORT || 3000}/graphql`,
                headers: {}
            })
        : createHttpLink({
            uri: '/graphql',
            headers: {}
        });
    const createDirectLink = () => createHttpLink({
        uri: graphQLURI,
        headers: {
            token: token,
            shopid: shopid
        }
    });
    const apiLink = enableGateway ? createGatewayLink() : createDirectLink();
    const channelMiddleware = new ApolloLink((operation, forward) => {
        // Sets the current channel before each new HTTP request is made
        if (channel) {
            operation.setContext(({ headers = {} }) => ({
                headers: Object.assign({}, headers, { currency: channel.currency.id, channelid: channel.id, culture: channel.language.culture, country: channel.country.code, name: channel.name })
            }));
        }
        return forward(operation);
    });
    const authMiddleware = new ApolloLink((operation, forward) => {
        // Sets the auth token when a new request is made
        loadAuthToken();
        operation.setContext(({ headers = {} }) => ({
            headers: Object.assign({}, headers, { authorization: authToken ? `Bearer ${authToken}` : '' })
        }));
        return forward(operation);
    });
    const previewMiddleware = new ApolloLink((operation, forward) => {
        operation.setContext(({ headers = {} }) => ({
            headers: Object.assign({}, headers, { preview })
        }));
        return forward(operation);
    });
    const errorMiddleware = onError(({ graphQLErrors, networkError, operation }) => {
        if (graphQLErrors) {
            graphQLErrors.map(error => {
                const code = get(error, 'extensions.code');
                if (code === 'EmptyRoute') {
                    console.log(`[404 error at ${operation.variables.path}]`);
                    report(new Error(`MissingRoute: Attempted to access ${operation.variables.path}`));
                }
                else {
                    console.log(code);
                    // Other graph errors will be logged to Sentry
                    report(new Error(`${error.message || code}`), {
                        // Include all operation details (query etc)
                        operation
                    });
                }
            });
        }
        // 503
        if (networkError) {
            console.log(`[Network error]: ${networkError}`);
            report(networkError);
        }
    });
    const { link: networkStatusNotifierLink, NetworkStatusNotifier } = createNetworkStatusNotifier();
    const links = [
        errorMiddleware,
        channelMiddleware,
        authMiddleware,
        previewMiddleware,
        networkStatusNotifierLink,
        apiLink
    ];
    if (enableGateway) {
        links.unshift(createPersistedQueryLink({ useGETForHashedQueries: true }));
    }
    const link = ApolloLink.from(links);
    const cache = createCache();
    const client = new ApolloClient({
        link,
        assumeImmutableResults: true,
        cache: __IN_SERVER__
            ? cache
            : cache.restore(window.__APOLLO_STATE__),
        ssrMode: !!__IN_SERVER__
    });
    return { client, NetworkStatusNotifier };
};
//# sourceMappingURL=apollo.js.map